import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  ButtonProps,
  Checkbox,
  ConfigProvider,
  Form,
  Input,
  List,
  Modal,
  notification,
  Typography,
} from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { ConstraintOperator } from '@/common/ConstraintsEditor/Constraint/facetMap';
import { scribeInstrumentationEvents } from '@/common/Scribe/EventsList';
import { HTTP_STATUS } from '@/constants';
import { TIMEZONE } from '@/constants/dateTime';
import { FACETS } from '@/constants/facets';
import { SystemEnvironment } from '@/constants/systemEnviroment';
import {
  useGetAllActiveBlackdays,
  useGetUpcomingBlackday,
} from '@/hooks/usePlannerQueries/blackday';
import { useApproveDiff } from '@/hooks/usePlannerQueries/diff';
import { useApproveExperiment } from '@/hooks/usePlannerQueries/experiment';
import {
  Experiment,
  UpdateStateResponse,
  VariableInfo,
} from '@/lib/PlannerDTOs';
import { useAppNavigate } from '@/utils/navigation';
import { getSystemEnvironment } from '@/utils/system-environment';
import { formatHasAccessErrorGeneric } from '@/utils/url';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const RolloutChecklist = ({
  onChange,
}: {
  onChange: (values: boolean[]) => void;
}) => {
  const location = useLocation();

  const isExperimentView = location?.pathname?.includes('/experiments/');

  if (isExperimentView) return null;

  const checklist: string[] = [
    'Check with developers that the new values are within expected range and type.',
    'For production change, make sure that the changes are tested and verified on staging first.',
  ];

  const [checkListValues, setCheckListValues] = useState<boolean[]>(
    checklist.map(() => false),
  );

  const debounceHandleCheclistChange = useMemo(
    () =>
      _.debounce((values: boolean[]) => {
        onChange(values);
      }, 500),
    [],
  );

  const handleChecklistChange = (e, ind) => {
    let newV: boolean[] = [...checkListValues];
    newV[ind] = e.target.checked;
    setCheckListValues(newV);
    debounceHandleCheclistChange(newV);
  };

  return (
    <Alert
      message="Please make sure you have done the following before saving this change."
      type="info"
      closable={false}
      description={
        <List
          dataSource={checklist}
          renderItem={(l, ind) => (
            <List.Item className={'change-request-approve-checklist-item'}>
              <Checkbox onChange={(e) => handleChecklistChange(e, ind)}>
                {l}
              </Checkbox>
            </List.Item>
          )}
        />
      }
    />
  );
};

interface ApproveChangeRequestProps {
  experiment?: Experiment;
  variable?: VariableInfo;
  diffId?: string;
  disabled?: boolean;
  onSuccess?: () => void;
}

const ApproveChangeRequest = ({
  type,
  shape,
  ghost,
  size = 'small',
  icon,
  experiment,
  variable,
  diffId,
  disabled,
  style,
  onSuccess: onOk = () => {},
}: ButtonProps & ApproveChangeRequestProps) => {
  const { navigateTo } = useAppNavigate();
  const { pathname } = location;

  const [open, setOpen] = useState<boolean>(false);
  const [enableApprove, setEnableApprove] = useState<boolean>(!!experiment);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const isExperimentView = !variable;
  const { data: upcomingBlackday, isLoading: loadingUpcomingBlackday } =
    useGetUpcomingBlackday({
      enabled: !isExperimentView,
    });

  const { data: allActiveBlackdays, isLoading: loadingAllActiveBlackdays } =
    useGetAllActiveBlackdays({
      enabled: isExperimentView,
    });

  const { mutate: approveDiff } = useApproveDiff({
    onSuccess: () => {
      setOpen(false);
      onOk();
      notification.success({
        description: `Rollout changes were successfully approved and published`,
        message: 'Approval of rollout',
        onClose: () => {
          navigateTo(pathname.replace(`/approve-request/${diffId}`, ''), {
            replace: true,
          });
        },
        duration: 2,
      });
    },
    onError: (err: any) => {
      const errMessage = () => {
        if (err?.response?.data?.arg) {
          const message: string = err.response.data.arg;
          return message.replace('\n', ', ');
        }

        if (err?.response.status === HTTP_STATUS.FORBIDDEN) {
          return 'Not enough permissions';
        }

        return 'unknown error';
      };

      const description = `Rollout changes were not approved  
            due to: ${errMessage()}`;

      notification.error({
        description,
        message: 'Approval of rollout',
      });
    },
  });

  const { mutate: approveExperiment } = useApproveExperiment({
    onSuccess: (res: UpdateStateResponse) => {
      scribe.sendEvent(
        scribeInstrumentationEvents.clickApproveExperiment.name,
        {
          experiment: experiment?.name,
          status: experiment?.state,
          STATE_NAME: scribeInstrumentationEvents.clickApproveExperiment.state,
        },
      );

      setOpen(false);

      if (res?.scheduled) {
        notification.success({
          message: 'Approving an Experiment',
          description:
            'Approval flow is complete. The experiment is successfully scheduled.',
        });
      } else {
        notification.success({
          message: 'Approving an Experiment',
          description: 'Experiment was successfully approved.',
        });
      }

      onOk();
    },
    onError: (err: any) => {
      let description = (
        <span>
          Experiment request was not scheduled, due to: {err.toString()}
        </span>
      );
      if (err?.response?.status === HTTP_STATUS.FORBIDDEN) {
        description = formatHasAccessErrorGeneric(err);
      }
      notification.error({
        description,
        message: 'Approving an Experiment',
      });
    },
  });

  const objectIsWithinBlackday = (): boolean => {
    // Check if environment is staging and return false
    if (getSystemEnvironment() === SystemEnvironment.STAGING) return false;

    if (isExperimentView) {
      if (!allActiveBlackdays?.length) return false;

      const constraints = experiment?.constraints;
      let experimentStart = null;
      let experimentEnd = null;
      let isWithinBlackday = false;
      // Get experiment start and end time from constraints
      if (constraints?.length > 0) {
        for (let i = 0; i < constraints.length; i += 1) {
          const constraint = constraints[i];
          if (constraint?.target === FACETS.Timestamp) {
            if (constraint?.op === ConstraintOperator.LESS_THAN.value) {
              experimentEnd = dayjs
                .unix(parseFloat(constraint.value))
                .tz(TIMEZONE);
            } else if (
              constraint?.op === ConstraintOperator.GREATER_THAN.value
            ) {
              experimentStart = dayjs
                .unix(parseFloat(constraint.value))
                .tz(TIMEZONE);
            }
          }
        }
      }

      for (let i = 0; i < allActiveBlackdays?.length; i += 1) {
        const blackday = allActiveBlackdays[i];
        const startOfBlackday = dayjs(blackday?.start_time).tz(TIMEZONE);
        const endOfBlackday = dayjs(blackday?.end_time).tz(TIMEZONE);

        if (
          experimentStart?.isBetween(startOfBlackday, endOfBlackday) ||
          experimentEnd?.isBetween(startOfBlackday, endOfBlackday)
        ) {
          isWithinBlackday = true;
          break;
        }
      }

      return isWithinBlackday;
    }

    if (!upcomingBlackday) return false;

    const startOfBlackday = dayjs(upcomingBlackday?.start_time).tz(TIMEZONE);
    const endOfBlackday = dayjs(upcomingBlackday?.end_time).tz(TIMEZONE);
    const now = dayjs().tz(TIMEZONE);

    return now.isBetween(startOfBlackday, endOfBlackday);
  };

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setIsFormValid(true);
      },
      () => {
        setIsFormValid(false);
      },
    );
  }, [values]);

  const handleOk = () => {
    if (isExperimentView) {
      approveExperiment([experiment?.name, false, values?.slack_approval_link]);
      return;
    }

    approveDiff([variable?.name, diffId, values?.slack_approval_link]);
  };

  return (
    <>
      <Button
        disabled={
          disabled || loadingAllActiveBlackdays || loadingUpcomingBlackday
        }
        type={type}
        ghost={ghost}
        size={size}
        shape={shape}
        onClick={() => {
          if (objectIsWithinBlackday()) {
            setOpen(true);
            return;
          }

          if (variable) {
            setOpen(true);
            return;
          }

          handleOk();
        }}
        style={style}
        icon={icon}
        loading={loadingAllActiveBlackdays || loadingUpcomingBlackday}
      >
        {isExperimentView ? 'Approve' : 'Approve & Publish Changes'}
      </Button>
      {open && (
        <Modal
          title="Confirm Changes"
          open={open}
          centered
          maskClosable={false}
          mask
          width={700}
          cancelButtonProps={{
            size: 'small',
          }}
          onCancel={() => {
            setOpen(false);
            setEnableApprove(false);
            form.resetFields();
          }}
          okButtonProps={{
            size: 'small',
            disabled: !enableApprove || !isFormValid,
          }}
          onOk={() => {
            handleOk();
          }}
          okText={isExperimentView ? 'Approve' : 'Publish Changes'}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!isExperimentView && (
              <RolloutChecklist
                onChange={(values) => {
                  setEnableApprove(values.every((value) => value));
                }}
              />
            )}
            {objectIsWithinBlackday() && (
              <ConfigProvider
                theme={{
                  components: {
                    Form: {
                      itemMarginBottom: 0,
                    },
                  },
                }}
              >
                <>
                  <Form
                    form={form}
                    initialValues={{
                      slack_approval_link: '',
                    }}
                    layout="vertical"
                  >
                    <Form.Item
                      name="slack_approval_link"
                      label="Slack Approval Link"
                      required
                      rules={[
                        {
                          required: true,
                          message: 'Please input slack approval link',
                        },
                        {
                          pattern: new RegExp(
                            '^https://grab\\.slack\\.com/archives/',
                          ),
                          message:
                            'Please input a valid slack approval link. It begins with https://grab.slack.com/archives',
                        },
                      ]}
                    >
                      <Input data-testid="slack-approval-link-input" />
                    </Form.Item>
                  </Form>
                  <SecondaryText>
                    The slack URL of getting confirmation from oncall lead for
                    choosing this pipeline. It begins with
                    https://grab.slack.com/archives
                  </SecondaryText>
                </>
              </ConfigProvider>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

const SecondaryText = styled(Typography.Metadata)`
  color: #a3a3a3;
`;

export default ApproveChangeRequest;
